/// <amd-module name="Core/Medius.Core.Web/Scripts/components/legalArchive/showLegalArchiveInformationContainer"/>
import * as React from "react";
import { useState, useEffect } from "react";
import { Button as ButtonProgress } from "@progress/kendo-react-buttons";
import { store, RootState } from "Core/Medius.Core.Web/Scripts/shared/store/reduxStore";
import { Provider, useSelector } from "react-redux";
import { Module } from "Core/Medius.Core.Web/Scripts/Medius/apps/reactSpa/app";
import { getLabelTranslation, getFormattedLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { CountryCodes } from "Core/Medius.Core.Web/Scripts/lib/countryCodes";
import { Text, Button, ModalDialog, Notification, ResponsiveGridLayout, GridLayoutItem, ColumnNumberEnum, AsyncButton } from "@medius/ui-controls";
import * as rest from "Core/Medius.Core.Web/Scripts/Medius/core/communication/remediumApi";
import { ModalDialogProperties } from "./ModalDialogProperties";
import { ArchivedDocumentContent } from "./ArchivedDocumentContent";
import { ArchivedDocument } from "./ArchivedDocument";
import { getCultureCode } from "Core/Medius.Core.Web/Scripts/Medius/lib/utils/user";

export default function ShowLegalArchiveInformationContainer(props: { isInArchive: boolean, isInLegalArchive: boolean, isPastRetentionPeriod: boolean, documentId: number, companyId: number }) {
    return <Provider store={store}>
        <ShowLegalArchiveInformationModal
            isInArchive={props.isInArchive}
            isInLegalArchive={props.isInLegalArchive}
            isPastRetentionPeriod={props.isPastRetentionPeriod}
            documentId={props.documentId}
            companyId={props.companyId} />
    </Provider>;
}

const ShowLegalArchiveInformationModal = (props: { isInArchive: boolean, isInLegalArchive: boolean, isPastRetentionPeriod: boolean, documentId: number, companyId: number }) => {

    const closeNotPostDialog = () => setDialogNotPostOpen(false);
    const openNotPostDialog = () => setDialogNotPostOpen(true);
    const [dialogNotPostOpen, setDialogNotPostOpen] = useState(false);

    const closePostDialog = () => setDialogPostOpen(false);
    const openPostDialog = () => setDialogPostOpen(true);
    const [dialogPostOpen, setDialogPostOpen] = useState(false);

    const closeRemovedDialog = () => setDialogRemovedOpen(false);
    const openRemovedDialog = () => setDialogRemovedOpen(true);
    const [dialogRemovedOpen, setDialogRemovedOpen] = useState(false);

    const [archivedDocumentInformation, setArchivedDocumentInformation] = useState<ArchivedDocument>({} as ArchivedDocument);
    const [notPosteDocumentInformation, setNotPostedDocumentInformation] = useState("");
    const isLegalArchiveEnabled = useSelector((state: RootState) => state.accesses?.enabledModules.includes(Module.LegalArchive) ?? false);

    const downloadFile = (documentId: number) => {
        rest.get(`remedium-api/apa/legal-archive/documents/download?documentId=${documentId}`)
            .then(response => {
                return response.blob().then((content: any) => content);
            })
            .then(content => {
                const anchor = document.createElement("a");
                anchor.href = window.URL.createObjectURL(content);
                anchor.download = archivedDocumentInformation?.metadata?.["FileName"] ?? "file";
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
            });
    };

    useEffect(() => {
        if (dialogRemovedOpen || dialogPostOpen) {
            rest.get(`remedium-api/apa/legal-archive/documents?documentId=${props.documentId}`)
                .then(response => response.json())
                .then(content => {
                    const storageRegionFullName = CountryCodes.find(x => x.Value === content?.storageRegion);
                    content.storageRegion = storageRegionFullName?.Name ?? content.storageRegion;
                    setArchivedDocumentInformation(content);
                });
        } else {
            setArchivedDocumentInformation({} as ArchivedDocument);
        }
    }, [dialogPostOpen, dialogRemovedOpen, props.documentId]);

    useEffect(() => {
        if (dialogNotPostOpen) {
            rest.get(`remedium-api/apa/legal-archive/storage-locations/company?companyId=${props.companyId}`)
                .then(response => response.json())
                .then(content => {
                    const companyDocumentsStorageLocalizationFullName = CountryCodes.find(x => x.Value === content.storageLocationISO).Name;
                    setNotPostedDocumentInformation(
                        getFormattedLabelTranslation(
                            "#Enterprise/legalArchiveInvoiceIsNotPostInERPSystemMessage", [`${companyDocumentsStorageLocalizationFullName}`, `${content.retentionYears}`]));
                });
        } else {
            setNotPostedDocumentInformation("");
        }
    }, [dialogNotPostOpen, props.companyId]);

    if (!isLegalArchiveEnabled) {
        return <></>;
    }

    if (props.isInArchive && !props.isInLegalArchive) {
        return <></>;
    }

    if (props.isInLegalArchive) {
        if (props.isPastRetentionPeriod)
            return <RenderLegalArchivePostedButRetentionPeriodExpiredButton
                openDialog={openRemovedDialog}
                closeDialog={closeRemovedDialog}
                dialogOpen={dialogRemovedOpen}
                content={archivedDocumentInformation} />;

        return <RenderLegalArchivePostedButton
            openDialog={openPostDialog}
            closeDialog={closePostDialog}
            dialogOpen={dialogPostOpen}
            downloadAction={() => downloadFile(props.documentId)}
            content={archivedDocumentInformation} />;
    } else {
        return <RenderLegalArchiveNotPostedButton
            openDialog={openNotPostDialog}
            closeDialog={closeNotPostDialog}
            dialogOpen={dialogNotPostOpen}
            message={notPosteDocumentInformation} />;
    }
};

const getLabelTranslationWithColonPostfix = (labelKey: string) => `${getLabelTranslation(labelKey)}:`;

const RenderActionsForModalDialogWithoutDownload = ({ closeAction }: { closeAction: () => void }) =>
    <Button label={getLabelTranslation("#Core/close")} onClick={closeAction} data-testid="legal-archive-details-modal-button-close" />;

const RenderActionsForModalDialog = ({ closeAction, downloadAction }: { closeAction: () => void, downloadAction: () => void }) =>
    <>
        <Button label={getLabelTranslation("#Core/close")} onClick={closeAction} data-testid="legal-archive-details-modal-button-close" />
        <AsyncButton label={getLabelTranslation("#Core/downloadFile")} onClick={downloadAction} data-testid="legal-archive-details-modal-button-download" />
    </>;

const RenderModalItem = ({ label, text, row, dataTestId }: { label: string, text: string, row: ColumnNumberEnum, dataTestId: string }) =>
    <>
        <GridLayoutItem row={row} column={1}>
            <Text data-testid={`${dataTestId}-label`} variant="bold-body">{label}</Text>
        </GridLayoutItem>
        <GridLayoutItem row={row} column={2} columnSpan={2}>
            <Text data-testid={`${dataTestId}-text`} variant="paragraph">{text}</Text>
        </GridLayoutItem>
    </>;

const storageDetailsTestId = "legal-archive-modal-storage-details";
const RenderStorageDetails = ({ content }: ArchivedDocumentContent) =>
    <ResponsiveGridLayout columnWidth={2} lockColumnWidths={true} itemColumnSpacing="vsmall" itemRowSpacing="vsmall" data-testid={storageDetailsTestId}>
        <RenderModalItem row={1} label={getLabelTranslationWithColonPostfix("#Core/storagePath")} text={content?.path} dataTestId={`${storageDetailsTestId}-storage-path`} />
        <RenderModalItem row={2} label={getLabelTranslationWithColonPostfix("#Core/creationDate")} text={content?.metadata?.InvoiceDate} dataTestId={`${storageDetailsTestId}-creation-date`} />
        <RenderModalItem row={3} label={getLabelTranslationWithColonPostfix("#Core/primaryStorage")} text={content?.storageRegion} dataTestId={`${storageDetailsTestId}-primary-storage`} />
        <RenderModalItem row={4} label={getLabelTranslationWithColonPostfix("#Core/retentionYears")} text={content?.retentionYears?.toString()} dataTestId={`${storageDetailsTestId}-retention-years`} />
        <RenderModalItem row={5} label={getLabelTranslationWithColonPostfix("#Core/retentionUntil")} text={new Date(content?.expirationDate).toDateString()} dataTestId={`${storageDetailsTestId}-retention-until`} />
    </ResponsiveGridLayout>;

const signatureDetailsTestId = "legal-archive-modal-signature-details";
const RenderSignatureDetails = ({ content }: ArchivedDocumentContent) =>
    <ResponsiveGridLayout columnWidth={2} lockColumnWidths={true} itemColumnSpacing="vsmall" itemRowSpacing="vsmall" data-testid={signatureDetailsTestId}>
        <RenderModalItem row={1} label={getLabelTranslationWithColonPostfix("#Enterprise/signatory")} text={content?.signature?.signatory} dataTestId={`${signatureDetailsTestId}-signatory`} />
        <RenderModalItem row={2} label={getLabelTranslationWithColonPostfix("#Core/date")} text={new Date(content?.signature?.date).toDateString()} dataTestId={`${signatureDetailsTestId}-date`} />
        <RenderModalItem row={3} label={getLabelTranslationWithColonPostfix("#Core/AddressLocation")} text={content?.signature?.location} dataTestId={`${signatureDetailsTestId}-location`} />
        <RenderModalItem row={4} label={getLabelTranslationWithColonPostfix("#Enterprise/reason")} text={content?.signature?.reason} dataTestId={`${signatureDetailsTestId}-reason`} />
    </ResponsiveGridLayout>;

const certificateDetailsTestId = "legal-archive-modal-certificate-details";
const RenderCertificateDetails = ({ content }: ArchivedDocumentContent) =>
    <ResponsiveGridLayout columnWidth={2} lockColumnWidths={true} itemColumnSpacing="vsmall" itemRowSpacing="vsmall" data-testid={certificateDetailsTestId}>
        <RenderModalItem row={1} label={getLabelTranslationWithColonPostfix("#Core/emailSubject")} text={content?.signature?.certificate?.subject} dataTestId={`${certificateDetailsTestId}-subject`} />
        <RenderModalItem row={2} label={getLabelTranslationWithColonPostfix("#Core/issuer")} text={content?.signature?.certificate?.issuer} dataTestId={`${certificateDetailsTestId}-issuer`} />
        <RenderModalItem row={3} label={getLabelTranslationWithColonPostfix("#Core/serialNumber")} text={content?.signature?.certificate?.serialNumber} dataTestId={`${certificateDetailsTestId}-serial-number`} />
        <RenderModalItem row={4} label={getLabelTranslationWithColonPostfix("#Enterprise/startDate")} text={new Date(content?.signature?.certificate?.validityStartDate).toDateString()} dataTestId={`${certificateDetailsTestId}-start-date`} />
        <RenderModalItem row={5} label={getLabelTranslationWithColonPostfix("#Enterprise/endDate")} text={new Date(content?.signature?.certificate?.validityEndDate).toDateString()} dataTestId={`${certificateDetailsTestId}-end-date`} />
    </ResponsiveGridLayout>;

const metadataDetailsTestId = "legal-archive-modal-metadata-details";
const RenderMetadataDetails = ({ content }: ArchivedDocumentContent) =>
    <ResponsiveGridLayout columnWidth={2} lockColumnWidths={true} itemColumnSpacing="vsmall" itemRowSpacing="vsmall" data-testid={metadataDetailsTestId}>
        <RenderModalItem row={1} label={getLabelTranslationWithColonPostfix("#Core/invoiceId")} text={content?.metadata?.["InvoiceId"]} dataTestId={`${metadataDetailsTestId}-invoice-id`} />
        <RenderModalItem row={2} label={getLabelTranslationWithColonPostfix("#Core/companyId")} text={content?.metadata?.["CompanyId"]} dataTestId={`${metadataDetailsTestId}-company-id`} />
        <RenderModalItem row={3} label={getLabelTranslationWithColonPostfix("#Enterprise/MobileSiteInvoiceNumber")} text={content?.metadata?.["InvoiceNumber"]} dataTestId={`${metadataDetailsTestId}-invoice-number`} />
        <RenderModalItem row={4} label={getLabelTranslationWithColonPostfix("#Enterprise/invoiceDate")} text={new Date(content?.metadata?.["InvoiceDate"]).toDateString()} dataTestId={`${metadataDetailsTestId}-invoice-date`} />
    </ResponsiveGridLayout>;

const RenderRegulationNote = () =>
    <Text variant="paragraph">
        {getLabelTranslation("#Enterprise/legalArchiveLegalNoteForAzureUE")}
    </Text>;

const RenderModalContentWhenDocumentWasNotPosted = ({ message }: { message: string }) =>
    <Notification
        feedbackType="warning"
        title=""
        text={`${getLabelTranslation("#Enterprise/legalArchiveInvoiceIsNotPostInERPSystemTitle")} ${message}`}
        data-testid="legal-archive-modal-notification-when-document-was-not-posted">
    </Notification>;

const RenderModalContentWhenDocumentWasPosted = ({ content }: ArchivedDocumentContent) =>
    <>
        <h4>{getLabelTranslation("#Enterprise/storageDetails")}</h4>
        <RenderStorageDetails content={content} />
        <br />

        {
            content?.signature &&
            <>
                <h4>{getLabelTranslation("#Core/signature")}</h4>
                <RenderSignatureDetails content={content} />
                <br />
            </>
        }
        
        {
            content?.signature?.certificate &&
            <>
                <h4>{getLabelTranslation("#Core/certificate")}</h4>
                <RenderCertificateDetails content={content} />
                <br />
            </>
        }

        <h4>{getLabelTranslation("#Core/metadata")}</h4>
        <RenderMetadataDetails content={content} />
        <br />
        <RenderRegulationNote />
    </>;

const RenderModalContentWhenDocumentWasRemovedFromStorage = ({ content }: ArchivedDocumentContent) =>
    <Notification
        feedbackType="info"
        title=""
        text={getFormattedLabelTranslation("#Enterprise/legalArchiveInvoicePastRetentionPeriod", [new Date(content?.expirationDate).toLocaleDateString(getCultureCode(), { year: 'numeric', month: 'long', day: 'numeric' })])}
        data-testid="legal-archive-modal-notification-when-document-was-removed-from-storage">
    </Notification>;

const RenderLegalArchivePostedButton = ({ openDialog, closeDialog, dialogOpen, downloadAction, content }: ModalDialogProperties & { downloadAction: () => void }) =>
    <>
        <ButtonProgress data-testid="legal-archive-details-modal-button" icon="lock"
            title={getLabelTranslation("#Enterprise/legalArchivePostedTooltipMessage")}
            onClick={openDialog}>
            {getLabelTranslation("#Enterprise/legalArchive")}
        </ButtonProgress>

        <ModalDialog title={getLabelTranslation("#Enterprise/documentArchivingDetails")} width="medium"
            data-testid="legal-archive-details-modal-popup"
            isOpen={dialogOpen} onRequestClose={closeDialog} showActionsDivider={true}
            renderContent={() => RenderModalContentWhenDocumentWasPosted({ content })}
            renderActions={() => RenderActionsForModalDialog({ closeAction: closeDialog, downloadAction: downloadAction })} />
    </>;

const RenderLegalArchiveNotPostedButton = ({ openDialog, closeDialog, dialogOpen, message }: ModalDialogProperties) =>
    <>
        <ButtonProgress data-testid="legal-archive-details-modal-button" icon="unlock" fillMode="solid"
            title={getLabelTranslation("#Enterprise/legalArchiveNotPostedTooltipMessage")}
            onClick={openDialog}>
            {getLabelTranslation("#Enterprise/legalArchive")}
        </ButtonProgress>

        <ModalDialog title={getLabelTranslation("#Enterprise/documentArchivingDetails")} width="medium"
            data-testid="legal-archive-details-modal-popup"
            isOpen={dialogOpen} onRequestClose={closeDialog} showActionsDivider={true}
            renderContent={() => RenderModalContentWhenDocumentWasNotPosted({ message })}
            renderActions={() => RenderActionsForModalDialogWithoutDownload({ closeAction: closeDialog })} />
    </>;

const RenderLegalArchivePostedButRetentionPeriodExpiredButton = ({ openDialog, closeDialog, dialogOpen, content }: ModalDialogProperties) =>
    <>
        <ButtonProgress data-testid="legal-archive-details-modal-button" icon="clock"
            title={getLabelTranslation("#Enterprise/legalArchivePostedTooltipMessage")}
            onClick={openDialog}>
            {getLabelTranslation("#Enterprise/legalArchive")}
        </ButtonProgress>

        <ModalDialog title={getLabelTranslation("#Enterprise/documentArchivingDetails")} width="medium"
            data-testid="legal-archive-details-modal-popup"
            isOpen={dialogOpen} onRequestClose={closeDialog} showActionsDivider={true}
            renderContent={() => RenderModalContentWhenDocumentWasRemovedFromStorage({ content })}
            renderActions={() => RenderActionsForModalDialogWithoutDownload({ closeAction: closeDialog })} />
    </>;


